// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import { getSystemList } from "@awc/App/Store/Selectors/SystemListSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import translate from "@insite/client-framework/Translate";
import { TraitValueModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";

interface Props {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
    // XNG-Change
    availableTraitValueIds: Set<string>;
    quotedTraitValueIds: Set<string>;
    isPricingLoading?: boolean;
}

export interface ProductDetailsVariantGridStyles {
    labelText?: TypographyPresentationProps;
    traitsWrapper?: InjectableCss;
    colorTrait?: InjectableCss;
}

export const variantColorStyles: ProductDetailsVariantGridStyles = {};

const styles = variantColorStyles;

const ProductDetailsVariantCustomView = ({
    variantSelection,
    updateVariantSelection,
    variantTrait,
    // XNG-Change
    availableTraitValueIds,
    quotedTraitValueIds,
    isPricingLoading,
}: Props) => {
    const colors = useAppSelector(state => getSystemList(state as CustomAppState, "custom_ColorIcons"));
    const availableTraitValues = variantTrait.traitValues?.filter(traitValue =>
        availableTraitValueIds.has(traitValue.id),
    );
    const quotedTraitValues = variantTrait.traitValues?.filter(traitValue => quotedTraitValueIds.has(traitValue.id));

    // Select First Available Color
    useEffect(() => {
        const firstAvailable = availableTraitValues?.[0];
        if (firstAvailable && !variantSelection[variantTrait.id]) {
            updateVariantSelection(firstAvailable.id, variantTrait.id);
        }
    }, [availableTraitValues?.length]);

    const getMatchingSwatch = (colorName: string) => {
        const selectedColor = colors?.filter(color => color.name === colorName);
        return selectedColor?.length ? selectedColor[0].description : "";
    };

    const updateVariantSelectionSwatchGrid = (traitValueId?: string) => {
        updateVariantSelection(traitValueId ?? "", variantTrait.id);
    };

    const CreateSwatchGrid = (traitValue: TraitValueModel, isRequeiresQuoteColor: boolean) => {
        const swatchValue = getMatchingSwatch(traitValue.value);
        const swatchStyle = swatchValue.startsWith("http")
            ? { style: { backgroundImage: `url(${swatchValue})` } }
            : { color: swatchValue };

        return (
            <Clickable
                onClick={() => updateVariantSelectionSwatchGrid(traitValue.id)}
                disabled={traitValue.isDisabled}
                key={traitValue.id}
                aria-label={traitValue.value}
                data-test-selector={`productDetails_variantColor_${traitValue.value
                    .replace(/\s+/g, "_")
                    .toLowerCase()}`}
            >
                <StyledWrapper
                    title={variantTrait.displayTextWithSwatch ? traitValue.valueDisplay : undefined}
                    className={`${isRequeiresQuoteColor ? "quotedColor" : "availableColor"} ${
                        variantSelection[variantTrait.id] === traitValue.id ? "selected" : ""
                    }`}
                    {...swatchStyle}
                    {...styles.colorTrait}
                />
            </Clickable>
        );
    };

    const selectedColorName =
        variantTrait.traitValues?.find(traitValue => variantSelection[variantTrait.id] === traitValue.id)
            ?.valueDisplay || translate("Select Color");

    const hasSelection = !!variantSelection[variantTrait.id];

    // XNG-Change: hold the swatches until real-time pricing decides which colors are available vs. quoted,
    // otherwise they all render under "Requires Quote" and visibly regroup a moment later.
    if (isPricingLoading) {
        return null;
    }

    return (
        <StyledWrapper key={variantTrait.id} data-test-selector="productDetails_variantColors">
            {!!availableTraitValues?.length && (
                <>
                    <Typography {...styles.labelText}>
                        {`${translate("Available Colors")}: `}
                        {hasSelection &&
                            availableTraitValueIds.has(variantSelection[variantTrait.id] ?? "") &&
                            selectedColorName}
                    </Typography>
                    <StyledWrapper {...styles.traitsWrapper}>
                        {availableTraitValues.map(traitValue => CreateSwatchGrid(traitValue, false))}
                    </StyledWrapper>
                </>
            )}

            {!!quotedTraitValues?.length && (
                <>
                    <Typography {...styles.labelText} className="quotedVariantsLabel">
                        {`${translate("Requires Quote")}: `}
                        {!hasSelection
                            ? translate("Select Color")
                            : quotedTraitValueIds.has(variantSelection[variantTrait.id] ?? "") && selectedColorName}
                    </Typography>
                    <StyledWrapper {...styles.traitsWrapper}>
                        {quotedTraitValues.map(traitValue => CreateSwatchGrid(traitValue, true))}
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

export default ProductDetailsVariantCustomView;
